/**
 * This file was adapted from confluence/next/packages/search-utils/src/excerpt.tsx
 */

import React, { type ReactElement } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { PlatformIntegrationAri } from '@atlassian/ari/platform';
import type { SearchResultType } from '@atlassian/search-client/common/types';
import type {
	ThirdPartyRestrictionLevel,
	ThirdPartyUser,
} from '@atlassian/search-client/search-page/types';
import { type PersonCardProps } from '@atlassian/search-ui/card';

import { type NounKeys } from '../constants/nouns';
import {
	all3pProductKeys,
	is3pProductKey,
	type ProductKeys,
	type ProductKeys3P,
} from '../constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../constants/schemas/3p-config';

const SPECIAL_CHARS_REGEX = /^[!#$%^&*() ,.<>?\/\[\]_\-+=`~\|:;{}\\]+/;

const MAX_EXCERPT_LENGTH = 80;
export const replaceHtmlEntities: (inputStr: string) => string = (inputStr: string): string => {
	const htmlEntities: { [key: string]: string } = {
		'&nbsp;': ' ',
		'&lt;': '<',
		'&amp;': '&',
		'&quot;': '"',
		'&gt;': '>',
	};
	return Object.keys(htmlEntities).reduce((str, entity) => {
		return str.replace(new RegExp(entity, 'g'), htmlEntities[entity]);
	}, inputStr);
};

export const getWorkspaceNameFromProduct: (
	product: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => string | undefined = (
	product: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	let workspaceName;
	if (product && thirdPartyConfigs) {
		// Check if it's a 3P product or Forge connector
		const is3POrForge = is3pProductKey(product) || isForgeConnectorProductKey(product);
		if (is3POrForge) {
			const config = thirdPartyConfigs[product];
			const shouldDisplayWorkspaceName = config?.displayWorkspaceNameAsProductName;
			const shouldUseFanOutConnector = config?.fanOutConnectorSources;

			if (shouldDisplayWorkspaceName || shouldUseFanOutConnector) {
				workspaceName = config?.workspaceName;
			}
		}
	}
	return workspaceName;
};

/**
 * Make excerpt more readable by stripping leading non-alpha characters,
 * adding an ellipsis prefix and suffix, replacing line breaks with separators,
 * and bolding search terms.
 */

// TODO: deprecate this function in favor of enrichExcerpt
export const processExcerpt: (
	excerpt?: string | null,
	noLeadingEllipsis?: boolean,
	prefix?: string,
) => React.JSX.Element | React.JSX.Element[] = (
	excerpt?: string | null,
	noLeadingEllipsis?: boolean,
	prefix?: string,
): React.JSX.Element | React.JSX.Element[] => {
	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	excerpt = replaceHtmlEntities(excerpt).trim();

	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	const beginsWithHighlight = excerpt.slice(0, 8) === '@@@hl@@@';
	const beginsWithUrl =
		excerpt.slice(0, 4) === 'http' || (beginsWithHighlight && excerpt.slice(8, 12) === 'http');

	// Strip leading special characters unless the excerpt begins with a highlight
	if (!beginsWithHighlight) {
		excerpt = excerpt.replace(SPECIAL_CHARS_REGEX, '');
	}

	// Add ellipsis prefix if beginning of excerpt isn't a URL and excerpt length is greater than 80
	if (!noLeadingEllipsis && !beginsWithUrl && excerpt.length > MAX_EXCERPT_LENGTH) {
		excerpt = '...'.concat(excerpt);
	}

	// Add ellipsis suffix if excerpt doesn't end with ending punctuation and if length is greater than 80
	if (excerpt.length > MAX_EXCERPT_LENGTH && !excerpt.slice(-1).match(/[.!?]/)) {
		excerpt = excerpt.concat('...');
	}

	// Replace line breaks with separators
	excerpt = excerpt.replace(/\n+/g, ' · ');
	return enrichExcerpt(excerpt, prefix);
};

// Utility function to insert zero-width space every N characters
const insertZeroWidthSpace = (str: string, interval: number) => {
	let result = '';
	for (let i = 0; i < str.length; i++) {
		result += str[i];
		if ((i + 1) % interval === 0) {
			result += '\u200B';
		}
	}
	return result;
};

// Adding zero-width spaces to prevent overflow on long words or sequence of characters
export const processSegment: (segment: string) => string = (segment: string): string => {
	return segment
		.split(' ')
		.map((word) => {
			if (word.length > 20) {
				return insertZeroWidthSpace(word, 5);
			}
			return word;
		})
		.join(' ');
};
/**
 * Replaces highlight markers with <strong> tags.
 * Typically used for highlighting search terms in excerpts.
 */
export const enrichExcerpt: (
	excerpt?: string | null,
	prefix?: string,
) => React.JSX.Element | React.JSX.Element[] = (
	excerpt?: string | null,
	prefix?: string,
): React.JSX.Element | React.JSX.Element[] => {
	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	excerpt = replaceHtmlEntities(excerpt).trim();

	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	const res: ReactElement[] = [];
	let pre, textMatch;
	let i = 0;
	while (excerpt) {
		// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
		const match: RegExpMatchArray | null = excerpt.match(/(.*?)@@@hl@@@(.*?)@@@endhl@@@(.*)/s);
		if (!match) {
			res.push(
				<Box as="span" key={excerpt}>
					{processSegment(excerpt)}
				</Box>,
			);
			break;
		}
		[, pre, textMatch, excerpt] = match;
		if (pre) {
			res.push(
				<Box as="span" key={`pre-${i}`}>
					{processSegment(pre)}
				</Box>,
			);
		}
		if (textMatch) {
			res.push(
				<Box as="strong" key={`textMatch-${i}`}>
					{processSegment(textMatch)}
				</Box>,
			);
		}
		i++;
	}
	return (
		// NOTE: wrapping these in <div> previously broke focus rings
		// in search results
		<>
			{prefix}
			{res}
		</>
	);
};

export function isNonPublic(
	permissionLevel: ThirdPartyRestrictionLevel | string | undefined | null, // TODO QS-6952 typing here is not great
): permissionLevel is 'private' | 'restricted' {
	if (!permissionLevel) {
		return false;
	}
	return permissionLevel === 'restricted' || permissionLevel === 'private';
}

export const truncateDescription: (input: string, maxLength: number) => string = (
	input: string,
	maxLength: number,
): string => {
	if (input.length <= maxLength) {
		return input;
	}
	return input.substring(0, maxLength) + '...';
};

export const isAWSThumbnailUrlExpired: (thumbnailUrl: string) => boolean = (
	thumbnailUrl: string,
): boolean => {
	// Parse the URL
	try {
		const urlObj = new URL(thumbnailUrl);
		const params = urlObj.searchParams;

		// Extract the required parameters
		const amzExpires = params.get('X-Amz-Expires');
		const amzDate = params.get('X-Amz-Date');

		// if we lose the format, we should not attempt to render the thumbnail
		if (!amzExpires || !amzDate) {
			return true;
		}

		// Convert X-Amz-Date to a Date object
		const amzDateObj = new Date(
			parseInt(amzDate.substring(0, 4)), // Year
			parseInt(amzDate.substring(4, 6)) - 1, // Month (0-based index)
			parseInt(amzDate.substring(6, 8)), // Day
			parseInt(amzDate.substring(9, 11)), // Hour
			parseInt(amzDate.substring(11, 13)), // Minute
			parseInt(amzDate.substring(13, 15)), // Second
		);

		// Calculate the expiration time in milliseconds
		const expirationTime = amzDateObj.getTime() + parseInt(amzExpires) * 1000;

		// Get the current time in milliseconds
		const currentTime = Date.now();

		// Determine if the current time is greater than the expiration time
		return currentTime > expirationTime;
	} catch {
		// If the URL is invalid, we cannot determine expiration
		// TODO QS-6952 log exception for invalid URLs
		return true;
	}
};
type TimeSinceDate = {
	years: number;
	days: number;
	hours: number;
	minutes: number;
	months: number;
	seconds: number;
};

type RelativeTime = {
	unit: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
	value: number;
};

export const getRelativeTimeSince: (timestamp: Date | undefined) => RelativeTime = (
	timestamp: Date | undefined,
): RelativeTime => {
	return getTimeValuesWrapper(timeSinceDate(timestamp));
};

export const secondsSinceDate: (timestamp: Date | undefined) => number = (
	timestamp: Date | undefined,
): number => {
	if (!timestamp) {
		timestamp = new Date();
	}

	const now = new Date();
	return (now.getTime() - new Date(timestamp).getTime()) / 1000;
};
export const timeSinceDate: (timestamp: Date | undefined) => TimeSinceDate = (
	timestamp: Date | undefined,
): TimeSinceDate => {
	if (!timestamp) {
		timestamp = new Date();
	}
	const secondsPast = secondsSinceDate(timestamp);

	const timeUnits = {
		seconds: secondsPast,
		minutes: secondsPast / 60,
		hours: secondsPast / 3600,
		days: secondsPast / 86400,
		months: secondsPast / 2592000,
		years: secondsPast / 31536000,
	};

	return {
		seconds: Math.floor(timeUnits.seconds),
		minutes: Math.floor(timeUnits.minutes),
		hours: Math.floor(timeUnits.hours),
		days: Math.floor(timeUnits.days),
		months: Math.floor(timeUnits.months),
		years: Math.floor(timeUnits.years),
	};
};

export const getTimeValuesWrapper: ({
	seconds,
	minutes,
	hours,
	days,
	months,
	years,
}: {
	seconds: number;
	minutes: number;
	hours: number;
	days: number;
	months: number;
	years: number;
}) => RelativeTime = ({
	seconds,
	minutes,
	hours,
	days,
	months,
	years,
}: {
	seconds: number;
	minutes: number;
	hours: number;
	days: number;
	months: number;
	years: number;
}) => {
	return getTimeValues(seconds, minutes, hours, days, months, years);
};

export const getTimeValues: (
	seconds: number,
	minutes: number,
	hours: number,
	days: number,
	months: number,
	years: number,
) => RelativeTime = (
	seconds: number,
	minutes: number,
	hours: number,
	days: number,
	months: number,
	years: number,
): RelativeTime => {
	if (years > 0) {
		return { unit: 'year', value: years };
	} else if (months > 0) {
		return { unit: 'month', value: months };
	} else if (days > 0) {
		return { unit: 'day', value: days };
	} else if (hours > 0) {
		return { unit: 'hour', value: hours };
	} else if (minutes > 0) {
		return { unit: 'minute', value: minutes };
	} else {
		return { unit: 'second', value: seconds };
	}
};

export const getIntegrationKey: (integrationId?: string | null) => string | undefined = (
	integrationId?: string | null,
) => {
	try {
		const integrationKey = integrationId
			? PlatformIntegrationAri.parse(integrationId).integrationKey
			: undefined;

		if (integrationKey === 'jira-dc-secondary' || integrationKey === 'jira-dc-tertiary') {
			return 'jira-dc';
		}
		return integrationKey;
	} catch {
		// TODO QS-7368 log exception for invalid integration IDs
		return undefined;
	}
};

type UserWithMaybeDmInfo = {
	email?: string | null;
	externalId?: string | null;
};
/**
 * @returns information for the feature to Direct Message the user
 */
export const getExternalMessageDmIntegrationInfo: (
	integrationId?: string | null,
	user?: UserWithMaybeDmInfo,
) => PersonCardProps['externalMessageIntegration'] | undefined = (
	integrationId?: string | null,
	user?: UserWithMaybeDmInfo,
): PersonCardProps['externalMessageIntegration'] | undefined => {
	if (!integrationId || !user) {
		return;
	}

	const isSlack = integrationId === 'ari:cloud:platform::integration/slack';
	const isMsTeams = integrationId === 'ari:cloud:platform::integration/microsoft';

	if (isSlack) {
		const openDmUrl = getOpenInSlackUrl(user);
		if (openDmUrl) {
			return { integration: 'slack', openDmUrl };
		}
	} else if (isMsTeams) {
		const openDmUrl = getOpenInTeamsUrl(user);
		if (openDmUrl) {
			return { integration: 'ms-teams', openDmUrl };
		}
	}
};

/**
 * @returns user data for the person card
 */
export const getUserData: (
	thirdPartyUser?: ThirdPartyUser,
) => PersonCardProps['user'] | undefined = (
	thirdPartyUser?: ThirdPartyUser,
): PersonCardProps['user'] | undefined => {
	if (!thirdPartyUser || !thirdPartyUser.name) {
		return;
	}

	// We are waiting for this commit ticket to complete for jobTitle & profileUrl
	// https://hello.jira.atlassian.cloud/browse/COMMIT-14774

	return {
		name: thirdPartyUser.name,
		profilePicUrl: thirdPartyUser.picture ?? undefined,
		jobTitle: thirdPartyUser.extendedProfile?.jobTitle,
		profileUrl: null, // not available as of late Oct 2024
	};
};

/**
 * @returns Open in Slack URL for the user
 */
export const getOpenInSlackUrl: (user?: UserWithMaybeDmInfo) => string | undefined = (
	user?: UserWithMaybeDmInfo,
) => {
	const externalId = user?.externalId;
	if (!externalId) {
		return;
	}
	return `https://slack.com/app_redirect?channel=${externalId}`;
};

/**
 * @returns Open in MS Teams URL for the user
 */
export const getOpenInTeamsUrl: (user?: UserWithMaybeDmInfo) => string | undefined = (
	user?: UserWithMaybeDmInfo,
) => {
	const email = user?.email;
	if (!email) {
		return;
	}
	return `https://teams.microsoft.com/l/chat/0/0?users=${email}`;
};

export const getProductFromProviderId: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null,
	providerId: string | null | undefined,
) => ProductKeys3P | undefined = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null,
	providerId: string | null | undefined,
): ProductKeys3P | undefined => {
	if (!thirdPartyConfigs || !providerId) {
		return undefined;
	}

	// Find by providerId (dynamic API adds this field to configs, even for static Forge connectors)
	const [product] =
		Object.entries(thirdPartyConfigs).find(([, config]) => config?.providerId === providerId) || [];

	// Ensure the product is in our defined set or is a Forge connector so we can cast it safely
	if (
		product &&
		!all3pProductKeys.includes(product as ProductKeys3P) &&
		!isForgeConnectorProductKey(product)
	) {
		return undefined;
	}

	return product ? (product as ProductKeys3P) : undefined;
};

export const MICROSOFT_INTEGRATION_KEY = 'microsoft';
export const SHAREPOINT_RESOURCE_TYPES: string[] = ['document'];
export const TEAMS_RESOURCE_TYPES: string[] = ['message', 'conversation', 'video'];

/**
 * Resolves a ProductKeys3P for Microsoft results that share the same integrationAri.
 * Extracts the same resourceType-based disambiguation logic used in IconFor3P.
 */
export const getProductFromMicrosoftResourceType: (
	integrationKey: string | undefined,
	resourceType: string | null | undefined,
) => ProductKeys3P | undefined = (
	integrationKey: string | undefined,
	resourceType: string | null | undefined,
): ProductKeys3P | undefined => {
	if (integrationKey !== MICROSOFT_INTEGRATION_KEY || !resourceType) {
		return undefined;
	}
	if (SHAREPOINT_RESOURCE_TYPES.includes(resourceType)) {
		return 'sharepoint' as ProductKeys3P;
	}
	if (TEAMS_RESOURCE_TYPES.includes(resourceType)) {
		return 'teams' as ProductKeys3P;
	}
	return undefined;
};

export const get3PProductFromProviderIdOrIntegrationId: ({
	thirdPartyConfigs,
	providerId,
	integrationId,
	resourceType,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	providerId: string | null | undefined;
	integrationId: string | null | undefined;
	resourceType?: string | null;
}) => ProductKeys3P | undefined = ({
	thirdPartyConfigs,
	providerId,
	integrationId,
	resourceType,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	providerId: string | null | undefined;
	integrationId: string | null | undefined;
	resourceType?: string | null;
}): ProductKeys3P | undefined => {
	const integrationKey = getIntegrationKey(integrationId);
	return (
		getProductFromProviderId(thirdPartyConfigs, providerId) ||
		(fg('rovo_fps_show_app_name_in_search_metadata')
			? getProductFromMicrosoftResourceType(integrationKey, resourceType)
			: undefined) ||
		// Last-resort fallback: returns the raw integration key (e.g. 'slack', 'github').
		// For Microsoft products without a matching resourceType (e.g. OneDrive), this returns
		// 'microsoft' which has no displayName — intentional, as we can't disambiguate further.
		(integrationKey as ProductKeys3P | undefined)
	);
};

export const getProductDisplayNameFromProviderId: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	providerId?: string,
) => React.ReactNode = (thirdPartyConfigs: ThirdPartyConfigsBootstrap, providerId?: string) => {
	if (!providerId) {
		return undefined;
	}

	const [_id, config] =
		Object.entries(thirdPartyConfigs).find(([, config]) => config?.providerId === providerId) || [];

	if (!config) {
		return undefined;
	}

	return config.displayName;
};

export const filterSpacer: (node: React.ReactNode) => React.ReactNode = (
	node: React.ReactNode,
): React.ReactNode => {
	if (!React.isValidElement(node)) {
		return node;
	}

	// Check if element is a Spacer component
	if (typeof node.type === 'function' && (node.type as any).IS_METADATA_SPACER) {
		return null;
	}

	// If the element has children, filter out spacers
	if (node.props.children) {
		const children = React.Children.toArray(node.props.children);
		const filteredChildren = children.filter(
			(child) =>
				!(
					React.isValidElement(child) &&
					typeof child.type === 'function' &&
					(child.type as any).IS_METADATA_SPACER
				),
		);

		return React.cloneElement(node, node.props, ...filteredChildren);
	}

	return node;
};

export const isProductImpersonated: (
	product: ProductKeys3P | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => boolean = (
	product: ProductKeys3P | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	return !!(product && is3pProductKey(product)
		? thirdPartyConfigs[product]?.impersonationEnabled
		: false);
};

export const getJiraSearchResultTypeMetadata: (
	type: SearchResultType,
) =>
	| 'object'
	| 'space'
	| 'document'
	| 'spreadsheet'
	| 'presentation'
	| 'repository'
	| 'pull-request'
	| 'branch'
	| 'commit'
	| 'message'
	| 'page'
	| 'blogpost'
	| 'attachment'
	| 'whiteboard'
	| 'comment'
	| 'database'
	| 'embed'
	| 'folder'
	| 'calendar'
	| 'question'
	| 'issue'
	| 'incident'
	| 'filter'
	| 'plan'
	| 'board'
	| 'card'
	| 'goal'
	| 'tag'
	| 'work-item'
	| 'ask'
	| 'dashboard'
	| 'deal'
	| 'gliffy'
	| 'customer-org'
	| 'data-table'
	| 'type'
	| 'schema'
	| 'position'
	| 'learning'
	| 'channel'
	| 'email'
	| 'focus_area'
	| 'focus_area_status_update'
	| 'sharepoint-document'
	| 'unrecognised' = (type: SearchResultType) => {
	if (type === 'project') {
		return 'space';
	}
	return type;
};
